import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import {
  CmsIconConfig,
  FormValue,
  Logger,
  SectionBehavior,
  VerticalPositionBottom,
  VerticalPositionTop,
} from '../../global/_types';
import {
  CmsExtendedFormValue,
  CmsInputDropdownConfig,
  CmsMultiselectFormValue,
  determineType,
  getSectionBehavior,
  renderBlockMargin,
  textToHex,
  updateUrlParams,
} from '../../global/_utils';
import { CmsFilterMap, CmsFilterMapConfig, CmsMapMarker } from '../atomics/cms-filter-map/cms-filter-map';
import {
  calculateDistance,
  MapsLatLng,
  cmsLatLngFromGeoLocation,
  partnerCountries, cmsLngToLon,
} from '../../global/_googleMaps';
import GLightbox from 'glightbox';
import { CmsDataCook, CmsDataNews } from '../../global/_data';
import {
  CmsCustomConfigEvents,
  CmsFilterContentEventConfig,
} from '../atomics/cms-filter-content-event/cms-filter-content-event';
import { CmsDatePickerConfig } from '../atomics/cms-date-picker/cms-date-picker';
import { urlValueDelimiter } from '../../global/_constants';
import {
  CmsCustomConfigPartner,
  CmsFilterContentPartnerConfig,
} from '../atomics/cms-filter-content-partner/cms-filter-content-partner';

const FILTER_STRING = 'filter';
const FILTER_PREFIX_STRING = FILTER_STRING + '_';
const SORT_STRING = 'sort';
const LOCATION_STRING = 'zipKey';

@Component({
  tag: 'cms-filter-form',
  styleUrl: 'cms-filter-form.scss',
  shadow: true,
})

export class CmsFilterForm {
  @Element() host: HTMLElement;

  @Prop() config: string | CmsFilterFormConfig;
  @Prop() customConfig: string;
  @Prop() debug: boolean = false;
  @Prop() behavior: string = '{}';

  @State() dataLoaded: boolean = false;
  @State() showFilter: boolean = false;
  @State() results: any[];
  @State() loadResults: number = 0;
  @State() sortBy: string;

  private static defaultValues = {
    action: '',
    name: 'cmsFilterForm_' + Math.round(Math.random() * 100000),
    perPage: 10,
  }

  /* Config */
  private options: CmsFilterFormConfig;
  private customConfigEvent: CmsCustomConfigEvents;
  private customConfigPartner: CmsCustomConfigPartner;
  private datePickerConfig: CmsDatePickerConfig;

  private logger: Logger;

  /* SectionBehavior */
  private sectionBehavior: SectionBehavior;

  private lightbox: GLightbox;
  private useLightbox: boolean = false;

  private jsonData: any;
  private jsonDataMap: any[];
  private formData: FormData;
  private payload: CmsFilterServicePayload;
  private isServiceCall: boolean = false;
  private loading: boolean = true;
  private error: boolean = false;
  private initialized: boolean = false;
  private showResultsOnLoad: boolean = false;
  private activeMap: boolean = false;
  private updateMarker: number = 0;
  private resultsToShow: number = 0;
  private dataPage: number = 0;
  private initialSort: CmsSortConfig;
  private sortReverse: boolean = false;
  private filterDiv: HTMLDivElement;
  private zipKey: MapsLatLng;

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsFilterForm');

    this.options = {
      ...CmsFilterForm.defaultValues,
      ...(typeof (this.config) === 'string' ?
        JSON.parse(this.config) as CmsFilterFormConfig :
        this.config)
    } as CmsFilterFormConfig;

    // Global datepicker configuration
    this.datePickerConfig = {
      locale: this.options.locale || 'de-de',
      showFillDays: true,
      labels: {
        today: this.options.labels.datepickerToday || 'Today',
        prev: this.options.labels.datepickerPrev || 'prev',
        next: this.options.labels.datepickerNext || 'next'
      }
    } as CmsDatePickerConfig;

    this.loadCustomConfig();

    // FormData storage
    this.formData = new FormData();

    // Section-Behavior
    this.sectionBehavior = getSectionBehavior(this.behavior);

    // Use service or json file?
    this.isServiceCall = this.options.serviceUrl && this.options.serviceUrl!='';

    // Setup payload
    this.setupPayload();

    // Call filter service
    if (this.isServiceCall) {
      // Service call
      this.callData();
    }
    // Load data from JSON
    else if (!this.dataLoaded && this.options.jsonUrl) {
      fetch(this.options.jsonUrl)
        .then(response => response.json())
        .then(json => {
          this.jsonData = json;
          this.processData();
        })
        .catch(e => {
          this.showError('Failed to load json data: ' + e);
        });
    }

    // Map
    if (this.options.showMap && this.options.showMap === true && this.options.mapConfig) {
      this.options.mapConfig.parent = this.host.shadowRoot;
      this.options.mapConfig.showMap = this.options.showMap;
      this.options.mapConfig.type = this.options.type;
    } else {
      this.options.showMap = false;
    }

    // Lightbox for tiles buttonCallback
    this.useLightbox = this.options.type === 'cooks';
    if (this.useLightbox) {
      this.lightbox = GLightbox({
        elements: [],
        autoplayVideos: false,
        loop: false,
        draggable: false,
        skin: 'content'
      });
    }
  }

  componentWillRender() {
    this.activeMap = this.getActiveMapState();
  }

  componentDidLoad() {
    this.logger.trace('CmsFilterForm loaded');
  }

  private init() {
    this.formData = new FormData();
    this.resultsToShow = this.options.perPage;

    this.initialSort = this.getInitialSort();
    this.sortReverse = this.initialSort.reverse;

    // Service call will set results in response. Set result for json data file here:
    if (!this.isServiceCall) {
      this.results = this.jsonDataMap;
      this.sortBy = this.initialSort.key;
    }
  }

  private getInitialSort(): CmsSortConfig {
    return typeof (this.options.initialSort) === 'string'
      ? { key: this.options.initialSort, reverse: false } as CmsSortConfig
    : this.options.initialSort;
  }

  private loadCustomConfig() {
    this.showResultsOnLoad = true;
    // Parse custom config and add additional configuration
    switch (this.options.type) {
      case 'events':
        this.customConfigEvent = JSON.parse(this.customConfig) as CmsCustomConfigEvents;
        this.customConfigEvent.hashedLink = textToHex(window.location.href);
        this.customConfigEvent.color = this.options.color;
        break;
      case 'partner':
        this.customConfigPartner = JSON.parse(this.customConfig) as CmsCustomConfigPartner;
        if (!this.zipKey) {
          this.showResultsOnLoad = false;
        }
        break;
      case 'news':
        break;
      case 'cooks':
        break;
      default:
        break;
    }
  }

  private getActiveMapState(): boolean {
    if (!this.options.showMap) {
      return false;
    }
    const locationData = this.formData.get(LOCATION_STRING);
    return locationData != null
      && locationData !== ''
      && locationData !== 'undefined'
      && locationData !== 'null';
  }

  private showError(message: string) {
    this.loading = false;
    this.error = true;
    this.dataLoaded = true;
    this.logger.error(message);
  }

  /**
   * Fetch data via service
   * @private
   */
  private callData(redrawMarkers: boolean = false, context = null) {
    // Payload
    const loadMoreData = this.payload.page < this.dataPage;
    const locationChanged = this.payload.zipKey != this.zipKey;
    this.setPayload();
    //console.log(this.payload);
    // Fetch data by service call
    fetch(
      this.options.serviceUrl,
      {
        method: "POST",
        body: JSON.stringify(this.payload)
      }
    ).then(response => response.json())
      .then(json => {
        this.jsonData = json;
        if (!this.initialized) {
          this.processData();
          this.initialized = true;
        } else {
          // Get json data by map key
          if (!this.setJsonDataMap()) {
            return;
          }

          if (loadMoreData) {
            this.results = [...this.results, ...this.jsonDataMap];
          } else {
            this.results = this.jsonDataMap;
          }
          this.resultsToShow = this.results.length;
          // Update locations on map
          if (locationChanged) {
            // Set markers info on map
            this.setLocationMarker(context);
          } else if (redrawMarkers) {
            // Redraw markers
            this.updateMarker += 1;
          }
        }
      })
      .catch(e => {
        this.showError('Failed to load json data: ' + e);
      });
  }

  private setupPayload() {
    // Basic payload
    this.payload = {
      page: this.dataPage,
      amount: this.options.perPage ? this.options.perPage : 10,
      locale: this.options.locale,
      fsLocale: this.options.fsLocale,
      filter: {}
    };
    // Prefilters
    if (this.options.prefilter?.length > 0) {
      this.payload.preFilter = this.options.prefilter.reduce((acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      }, {});
    }
    // Available filter dropdowns
    if (this.options.filter?.length > 0) {
      this.payload.availableFilters = this.options.filter.map((filter) => {
        return {
          value: filter.dataConfig.value,
          label: Array.isArray(filter.dataConfig.value) ? null : filter.dataConfig.label,
          sortByValue: filter.sortByValue || false,
          sortReverse: filter.sortReverse || false,
        };
      });
    }
    // Initial sort
    this.payload.sort = this.getInitialSort();
  }

  private resetPayload() {
    // reset payload
    this.payload.amount = this.options.perPage ? this.options.perPage : 10;
    this.payload.page = 0;
    this.payload.filter = {};
    this.payload.zipKey = null;
    this.payload.sort = this.getInitialSort();
  }

  private setPayload() {
    // set payload: zipKey, page
    this.payload.page = this.dataPage;
    if (this.payload.zipKey != this.zipKey) {
      this.payload.zipKey = this.zipKey;
      this.payload.sort = null;
      this.payload.page = 0;
    }
  }

  private setJsonDataMap(): boolean {
    // Get json data by map key
    try {
      this.jsonDataMap = Array.from(this.options.jsonMapKey ? this.jsonData[this.options.jsonMapKey] : this.jsonData);
    } catch (e) {
      this.showError('Error while parsing data: ' + e);
      return false;
    }

    if (!Array.isArray(this.jsonDataMap)) {
      this.showError('Error! No data retrieved!');
      return false;
    } else if (this.getResultCount() < 1) {
      // Show no results message, next lines are redundant
      this.loading = false;
      this.dataLoaded = true;
    }

    // finally cast json data to type object
    this.castJsonData();

    return true;
  }

  /***
   * Prepare json data for further usage:
   * - get content related map
   * - preprocess json data e.g. merge map data
   * - prefilter map
   * - fill filter dropdowns with related data from json
   */
  private processData() {
    // Get json data by map key
    if (!this.setJsonDataMap()) {
      return;
    }

    if (this.isServiceCall) {
      // Set results initially
      this.results = this.jsonDataMap;
      this.loading = false;
      this.dataLoaded = true;
    } else {
      // Preprocess data by type
      this.preprocessDataByType();

      // Prefilter and store json data - user cannot change this filter
      this.prefilterData();
    }

    // Set initial results
    this.init();

    if (!Array.isArray(this.results)) {
      this.showError('Error! No results retrieved!');
      return;
    } else if (this.getResultCount() < 1) {
      // Show no results message, next lines are redundant
      this.loading = false;
      this.dataLoaded = true;
    }

    // Fill filter dropdowns
    this.setFilterDropdownValues();

    this.loading = false;
    this.dataLoaded = true;
  }

  private castJsonData() {
    // Static prefilter defined by type
    switch (this.options.type) {
      case 'events':
        this.jsonDataMap = this.jsonDataMap as CmsFilterContentEventConfig[];
        break;
      case 'partner':
        this.jsonDataMap = this.jsonDataMap as CmsFilterContentPartnerConfig[];
        break;
      case 'news':
        this.jsonDataMap = this.jsonDataMap as CmsDataNews[];
        break;
      case 'cooks':
        this.jsonDataMap = this.jsonDataMap as CmsDataCook[];
        break;
      default:
        break;
    }
  }

  private prefilterData() {
    // Static prefilter defined by type
    if (this.options.prefilter?.length > 0) {
      this.options.prefilter.forEach(prefilter => {
        if (Array.isArray(prefilter.value)) {
          // Process arrays of string or number
          let value = prefilter.value as any[];
          if (value.every(v => typeof (v) === 'string')) {
            // Convert strings to lowercase
            value = value.map(v => v.toLowerCase());
            this.jsonDataMap = this.jsonDataMap.filter(data =>
              (typeof (data[prefilter.key]) === 'string' && value.includes(data[prefilter.key].toLowerCase())) ||
              (Array.isArray(data[prefilter.key]) && value.some(r => data[prefilter.key].map(v => v.toLowerCase()).includes(r)))
            );
          } else {
            // Compare numbers
            this.jsonDataMap = this.jsonDataMap.filter(data => (value.includes(data[prefilter.key])));
          }
        } else if (typeof(prefilter.value) === 'string') {
          // Process strings: convert strings to lowercase
          const value = (prefilter.value as string).toLowerCase();
          this.jsonDataMap = this.jsonDataMap.filter(data =>
            (typeof (data[prefilter.key]) === 'string' && value.includes(data[prefilter.key].toLowerCase())));
        } else {
          // Compare booleans and number as types
          this.jsonDataMap = this.jsonDataMap.filter(data => (prefilter.value === data[prefilter.key]));
        }
      });
    }
  }

  private preprocessDataByType() {
    let countries = this.jsonData.partnerCountries
      ? this.jsonData.partnerCountries
      : partnerCountries;

    switch (this.options.type) {
      case 'events':
        // preprocess data if raw json file is loaded (not necessary for service call)
        // BEGIN used for FS preview
        // Show online events including today, others till now
        const compareDate = new Date();
        // Date: now
        const now = new Date();
        // Date: yesterday
        const yesterday = new Date();
        yesterday.setHours(0, 0, 0);
        yesterday.setDate(yesterday.getDate() - 1);
        // Filter
        this.jsonDataMap = this.jsonDataMap.filter(data => {
          compareDate.setTime(data.timestampDateTime);
          return data.isOnline ? compareDate > yesterday : compareDate > now;
        }) as CmsFilterContentEventConfig[];
        // Process locations
        this.jsonDataMap.forEach(data => {
          data.eventNameMapping = null;
          if (this.jsonData.eventNameMapping) {
            data.eventNameMapping = this.jsonData.eventNameMapping[data.typeUid] || null;
          }
          data.categories = (data.eventNameMapping && data.eventNameMapping.categories) ? data.eventNameMapping.categories : [];
          const location = this.jsonData.locations.filter(loc => loc.id === data.eventlocation)[0];
          data.location = location || null;
          if (data.location) {
            data.city = location?.city || null;
            data.country = location?.country || null;
            data.countryName = data.country ? countries[data.country] || data.country : null;
            data.location.countryName = data.countryName;
          }
        });
        this.jsonDataMap = this.jsonDataMap.filter(data => data.location);
        // END used for FS preview
        break;
      case 'partner':
        if (this.isServiceCall) {
          break;
        }
        // Process data
        const availableServices = new Set();
        this.jsonDataMap.forEach((data: CmsFilterContentPartnerConfig) => {
          // Find all available service filter for this data
          if (data.services) {
            Object.keys(data.services).filter(service => data.services[service])
              .forEach(service => availableServices.add(service));
          }
          // Set country name
          data.countryName = data.country ? countries[data.country] || data.country : null;
        });
        // Set available filters to dropdown
        if (availableServices) {
          const serviceFilter = this.options.filter.find(filter => filter.dataConfig.value === 'serviceFilter');
          if (serviceFilter) {
            serviceFilter.dropdown.values = this.customConfigPartner.services.flatMap(service =>
              availableServices.has(service.uid)
                ? { value: service.uid, label: service.label || service.uid, icon: service.icon } as CmsExtendedFormValue
                : []
            );
          }
          // Set certificates dropdown
          const filterTargetGroup = this.options.filter.find(filter => filter.dataConfig.value === 'targetGroup');
          if (filterTargetGroup) {
            filterTargetGroup.dropdown.values = filterTargetGroup.dropdown.values.filter(value =>
              this.jsonDataMap.some(result => result.targetGroup === value.value)
            );
          }
        }
        break;
      case 'cooks':
        this.jsonDataMap.forEach(data => {
          const hasName = data.name && data.name !== 'undefined' && data.name !== '';
          const hasFirstname = data.firstname && data.firstname !== 'undefined' && data.firstname !== '';
          data.headline = `${hasFirstname ? data.firstname : ''} ${hasName ? data.name : ''}`;
          data.text = null;
          //data.text = `<h4>${data.region.join(', ')}</h4>`;
          if (data.content?.button && !data.content.button.button) {
            data.content.button.button = { "layout": "default" };
          }
          data.buttonCallback = this.openLightbox.bind(this, data, { height: '85vh' });
        });
        break;
      default:
        break;
    }
  }

  private setFilterDropdownValues() {
    for (const entry of this.options.filter) {
      const valueKey = entry.dataConfig?.value;
      const labelKey = entry.dataConfig?.label || valueKey;

      if (!entry.dataConfig && !valueKey) {
        continue;
      }

      if (entry.dropdown?.values && entry.dropdown.values.length > 0) {
        entry.dropdown.values = entry.dropdown.values as CmsExtendedFormValue[];
      } else {
        let uniqueOptions = [];
        if (this.isServiceCall) {
          if (this.jsonData && this.jsonData['filters'] && this.jsonData['filters'][valueKey]) {
            uniqueOptions = this.jsonData['filters'][valueKey];
          }
        }
        else {
          const options = this.results.filter((result, index, array) => {
            const resultIndex = array.findIndex((b) => result[valueKey] === b[valueKey]);
            return index === resultIndex;
          }).flatMap(x => {
            if (Array.isArray(x[valueKey])) {
              return x[valueKey].map(value => ({ 'value': value, 'label': value }));
            } else {
              return { 'value': x[valueKey], 'label': x[labelKey] };
            }
          }).sort((a: any, b: any) => {
            const v1 = entry.sortByValue ? a.value : a.label;
            const v2 = entry.sortByValue ? b.value : b.label;
            switch (typeof (v1)) {
              case 'string':
                return v1.localeCompare(v2);
              case 'number':
                return v1 > v2 ? 1 : -1;
              case 'boolean':
                return Number(v1) - Number(v2);
              default:
                return 0;
            }
          });
          // Sort reverse if defined by config
          if (entry.sortReverse) {
            options.reverse();
          }
          // Remove duplicates
          uniqueOptions = options.filter((value, index, array) =>
            !array.filter((v, i) => JSON.stringify(value) == JSON.stringify(v) && i < index).length);
        }

        entry.dropdown.values = uniqueOptions as CmsExtendedFormValue[];
      }
      entry.dropdown.name = FILTER_PREFIX_STRING + valueKey;
    }

    // Remove empty filter and filter with just one option
    //this.options.filter = this.options.filter.filter(entry => entry.dropdown?.values && entry.dropdown.values.length > 1);
    // Remove empty filter and filter with just one option
    this.options.filter = this.options.filter.filter(entry => entry.dropdown?.values);

    // Show filter component if there is a filter set
    // show filter for sort option anyway
    if (this.options.filter.length > 0 || this.options.sort) {
      this.showFilter = true;
    }
  }

  /***
   * Sort json map and store to results
   * @private
   * @param newSortBy new sort by string
   * @param oldSortBy old sort by value
   * @param redrawMarkers force or stop redrawMarkers
   */
  @Watch('sortBy')
  sortResults(newSortBy: any, oldSortBy: any = null, redrawMarkers: boolean = true) {
    let sortBy = newSortBy || this.sortBy;
    sortBy = sortBy || this.initialSort.key;
    if (sortBy === this.initialSort.key) {
      this.sortReverse = this.initialSort.reverse;
    }
    if (sortBy == null || sortBy === '' || newSortBy === oldSortBy) {
      return;
    }

    if (this.isServiceCall) {
      this.payload.page = 0;
      this.payload.sort = {
        key: sortBy,
        reverse: this.sortReverse
      };
      this.callData(redrawMarkers);
    }
    else {
      // Sort results
      this.results.sort((a, b) => {
        switch (typeof (a[sortBy])) {
          case 'string':
            return a[sortBy].localeCompare(b[sortBy]);
          case 'number':
            return a[sortBy] > b[sortBy] ? 1 : -1;
          case 'boolean':
            return Number(a[sortBy]) - Number(b[sortBy]);
          default:
            return 0;
        }
      });

      // Sort reverse if defined in CmsInputDropdownConfig
      if (this.sortReverse) {
        this.results.reverse();
      }

      // Redraw markers
      this.updateMarker += 1;
    }
  }

  /***
   * Filter result array by dropdown values stored on formData
   * @private
   */
  private filterResults() {
    let filtered = this.jsonDataMap;
    const currentFilter = this.payload.filter;
    this.payload.filter = {};
    this.formData.forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX_STRING) && value && value !== 'undefined' && value !== 'null' && value !== '') {
        const valueString = value.toString();
        const valueList = valueString.split(urlValueDelimiter);
        const jsonKey = key.substring(FILTER_PREFIX_STRING.length, key.length);
        const jsonDateKey = 'timestamp';

        if (this.isServiceCall) {
          // Set filter to payload
          this.payload.filter[jsonKey] = valueList.length < 2 ? determineType(value) : valueList.map(x => determineType(x));
        }
        else {
          if (jsonKey == jsonDateKey + '_from' || jsonKey == jsonDateKey + '_to') {
            // Process datepicker
            filtered = filtered.filter(data => {
              if (Object.keys(data).includes(jsonDateKey) && data[jsonDateKey] !== 'undefined' && data[jsonDateKey] !== null) {
                if (jsonKey.endsWith('_to')) {
                  return (Number(data[jsonDateKey]) <= Number(valueString));
                }
                return (Number(data[jsonDateKey]) >= Number(valueString));
              }
            });
          } else {
            // Process all dropdown filter
            filtered = filtered.filter(data => {
              if (Object.keys(data).includes(jsonKey) && data[jsonKey] !== 'undefined' && data[jsonKey] !== null) {
                if (Array.isArray(data[jsonKey])) {
                  return (data[jsonKey].some(entry => valueList.includes(entry)));
                }
                return (valueList.includes(data[jsonKey].toString()));
              }
            });
          }
        }
      }
    });

    // Stop here for all service calls
    const nothingToFilter = Object.keys(this.payload.filter).length == 0 && Object.keys(currentFilter).length == 0;
    if (this.isServiceCall && !nothingToFilter) {
      this.callData(this.getActiveMapState());
      return;
    }

    // Set filtered results
    this.results = filtered;

    // Sort results again
    this.sortResults(this.sortBy, null);

    // Redraw markers
    this.updateMarker += 1;
  }

  private getResultCount(): number {
    // Special behaviour for partner filter
    if (this.options.type === 'partner' && !this.zipKey) {
      return 0;
    }

    if (this.isServiceCall) {
      return this.jsonData && this.jsonData['resultCount'] ? this.jsonData['resultCount'] : 0;
    } else if (this.results) {
      return this.results?.length;
    }
    return 0;
  }

  private getIsEndOfResults(): boolean {
    return this.isServiceCall ? this.jsonData && this.jsonData['isEndOfResults'] : this.getResultCount() <= this.resultsToShow;
  }

  private getResultsToShow(): number {
    return this.getIsEndOfResults() ? this.getResultCount() : this.resultsToShow;
  }

  /***
   * Render dropdowns with values
   * @param type type of the dropdown e.g. sort or filter
   * @param config the dependent dropdown config
   */
  private getComponentForType(type: CmsFilterFormFieldType, config: CmsFilterFormSelect): string {
    if(!this.dataLoaded) {
      return;
    }

    //config.form = this.options.name;
    switch (type.toString()) {
      case SORT_STRING:
        config.dropdown.name = SORT_STRING;
        config.dropdown.color = 'link-button';
        config.dropdown.icon = 'i-sort';
        return (
          <div class={`sort-button`} style={{ width: config.width, display: (this.activeMap ? 'none' : 'block') }}>
            <cms-input-dropdown debug={this.debug} config={config.dropdown} />
          </div>
        );
      case FILTER_STRING:
        config.dropdown.setUrlParams = this.options.setUrlParams;
        // hide special filter, when map is active
        const style = { flexBasis: config.width, display: (this.activeMap && config.hiddenByActiveMap ? 'none' : null) };

        if (config.dropdown.type == 'date') {
          return (
            <div style={style}>
              <cms-date-picker debug={this.debug} inputConfig={config.dropdown}
                               config={this.datePickerConfig}
              />
            </div>
          );
        }

        return (
          <div style={style}>
            <cms-input-dropdown debug={this.debug} config={config.dropdown} />
          </div>
        );
      default:
        return '';
    }
  }

  /**
   * First highlight rows
   * type=partner: Show RSM and REP
   * @private
   */
  private loadHighlightForType(): string {
    if (this.options.type !== 'partner' || !(this.customConfigPartner?.showRep || this.customConfigPartner?.showRsm)) {
      return '';
    }

    // Add REP as first result entry
    let rep: CmsFilterContentPartnerConfig;
    if (this.customConfigPartner.showRep) {
      const repRef  = this.results.find(item => item.rep != null && item.rep != '') as CmsFilterContentPartnerConfig;
      const repId = repRef?.rep;

      if (repId) {
        const repIndex = this.results.findIndex(item => item._id == repId);

        if (repIndex !== -1) {
          rep = this.results[repIndex];
          //this.results.splice(repIndex, 1);
        } else {
          rep = this.jsonDataMap.find(item => item._id == repId);
        }

        if (rep) {
          //this.results.unshift(rep);
          this.addMarker.emit({ marker: this.createMapMarker(rep) });
        }
      }
    }

    // Render RSM at top of list
    let rsm: CmsFilterContentPartnerConfig;
    if (this.customConfigPartner.showRsm) {
      rsm = this.results.find(item => item.rsmName != '' && (item.rsmEmail != '' || item.rsmPhone != '')) as CmsFilterContentPartnerConfig;
    }

    if (rsm || rep) {
      return (
        <div class="content highlight">
          {rsm ?
            <cms-filter-content-partner
              debug={this.debug}
              config={rsm}
              customConfig={this.customConfigPartner}
              rowIndex={-1}
              type={`rsm`}
              showMap={this.activeMap} />
            :''}
          {rep ?
            <cms-filter-content-partner
              debug={this.debug}
              config={rep}
              customConfig={this.customConfigPartner}
              rowIndex={-1}
              type={`rep`}
              showMap={this.activeMap} />
            :''}
        </div>
      );
    }
    return '';
  }

  private loadContentForType() {
    return this.results.slice(0, this.getResultsToShow()).map((config, i) => {
      switch (this.options.type) {
        case 'events':
          return (
            <cms-filter-content-event
              debug={this.debug}
              config={config}
              customConfig={this.customConfigEvent}
              rowIndex={i}
              showMap={this.activeMap} />
          );
        case 'partner':
          return (
            <cms-filter-content-partner
              debug={this.debug}
              config={config}
              customConfig={this.customConfigPartner}
              rowIndex={i}
              type={``}
              showMap={this.activeMap} />
          );
        case 'news':
        case 'cooks':
          return (
            <cms-tile
              debug={this.debug}
              config={this.customConfig} item={config}
            />
          );
        default:
          return '';
      }
    });
  }

  private openLightbox(data: any, props: {}) {
    const image = data.img ? `<img alt=\"${data.headline}\" src=\"${data.img}\" />` : '';
    const headline = data.headline ? `<h2>${data.headline}</h2>` : '';
    const region = data.region && data.region.length > 0 ? `<h5>${data.region.join(', ')}</h5>` : '';
    const sector = data.sector && data.sector.length > 0 ? `<h5>${data.sector.join(', ')}</h5>` : '';
    const subheadline = data.subheadline ? `<h4>${data.subheadline}</h4>` : '';
    const text = data.content?.text ? `<div>${data.content.text}</div>` : '';
    const button = data.content?.button ? `<div class='button'><cms-link-or-button config='${JSON.stringify(data.content?.button)}'/></div>` : '';
    let element = {
      'content': `${image}${headline}${subheadline}${sector}${region}${text}${button}`
    };
    if (props) {
      Object.assign(element, props);
    }
    this.lightbox.setElements([element]);
    this.lightbox.openAt(0);
  }

  private handleMoreButton() {
    if (this.isServiceCall) {
      this.dataPage += 1;
      this.callData();
    }
    else {
      this.updateMarker += 1;
      this.resultsToShow += this.options.perPage;
      this.loadResults += 1;
    }
  }

  @Listen('resetForm', { target: 'document' })
  handleReset() {
    // Reset url params
    this.formData.forEach((_value, key) => {
      updateUrlParams(key);
    });
    // Reset zipKey
    this.zipKey = null;
    // Reset data
    this.init();
    if (this.isServiceCall) {
      this.resetPayload();
      this.callData();
    }
  }

  private setFormData(formValue: FormValue): boolean {
    if (!this.formData.has(formValue.name)) {
      this.formData.append(formValue.name, formValue.value);
      return true;
    } else {
      if (this.formData.get(formValue.name) !== formValue.value) {
        this.formData.set(formValue.name, formValue.value);
        return true;
      }
    }
    return false;
  }

  @Listen('valueChanged')
  handleValueChanged(event: CustomEvent<CmsMultiselectFormValue>) {
    const element = event.detail as CmsMultiselectFormValue;
    const value = element.value;
    const valueList = element.valueList;
    element.value = valueList && valueList.length >= 0 ? valueList.join(urlValueDelimiter) : value;

    if (this.setFormData(element)) {
      // Sort dropdown handler
      if (element.name === SORT_STRING) {
        const params = element.params ? JSON.parse(element.params) : null;
        this.sortReverse = params != null && ('reverse' in params) && params.reverse;
        this.sortBy = value;
      }
      // Filter dropdown handler
      if (element.name.startsWith(FILTER_PREFIX_STRING)) {
        this.filterResults();
      }
      // Update url params
      if (this.options.setUrlParams) {
        updateUrlParams(element.name, element.value);
      }
    }
  }

  @Listen('locationChanged')
  locationChangedHandler(event: CustomEvent<CmsFilterMap>) {
    if (!event.detail.location || !this.options.showMap) {
      // Reset result sort after location input field is cleared
      //this.zipKey = null;
      this.sortResults(this.sortBy, null, false);
      return;
    }

    // Set zipKey
    this.zipKey = cmsLatLngFromGeoLocation(event.detail.location.geometry.location);

    // Sort by distance
    if (this.isServiceCall) {
      this.callData(false, event);
    }
    else {
      this.sortByDistance(event.detail.location.geometry.location);
      this.setLocationMarker(event);
    }
  }

  /**
   * Set custom infobox content for each type
   * @param event
   * @private
   */
  private setLocationMarker(event: CustomEvent<CmsFilterMap>) {
    if (!event) {
      return;
    }

    const marker = this.results.slice(0, this.getResultsToShow()).map(result => {
      return this.createMapMarker(result);
    });

    void event.detail.setMarker(marker);
  }

  private createMapMarker(result) {
    let id = result.id; // overwrite this id for other types if necessary
    let headline = this.options.labels.markerHeadline;
    let title: string;
    let location;
    let customContent: string;

    switch (this.options.type) {
      case 'events':
        // Location title
        location = { ...result.location };
        location.name = result.type;
        // Custom content
        const timeZone = this.options.mapConfig.showTimezone ? result.location.timezone : '';
        const time = `${result.starttime} ${(result.starttime !== result.endtime ? this.options.labels.dateRange + ' ' + result.endtime : ' ')}${timeZone}`;
        const date = `${result.startdate} ${(result.startdate !== result.enddate ? this.options.labels.dateRange + ' ' + result.enddate : ' ')}`;
        customContent = '<strong>' + date + '</strong><span>' + time + '</span>';
        break;
      case 'partner':
        id = result._id;
        title = result.name;
        location = { ...result };
        location.location = cmsLngToLon(location.location);
        if (this.customConfigPartner.services?.length > 0) {
          customContent = '<div class="services" style="grid-template-columns:repeat(' + this.customConfigPartner.services.length + ', auto)">';
          this.customConfigPartner.services.map((service: CmsIconConfig) => {
            const style = result.serviceFilter.includes(service.uid) ? 'active' : 'inactive';
            customContent = customContent.concat(`<img src=${service.icon} alt=${service.label} class=${style} />`);
          });
          customContent = customContent.concat('</div>');
        }
        if (result.targetGroup && result.targetGroup !== '') {
          const cert = this.customConfigPartner.certifications.find(item => item.color === result.targetGroup);
          if (cert) {
            customContent = customContent.concat('<div class="certs">');
            customContent = customContent.concat(`<img src=${cert.icon} alt=${cert.label} class="" />`);
            customContent = customContent.concat('</div>');
          }
        }
        break;
      default:
        location = { ...result.location };
        break;
    }

    return {
      id: id,
      title: title,
      headline: headline,
      location: location,
      customContent: customContent,
    } as CmsMapMarker;
  }

  private sortByDistance(sourceLocation) {
    switch (this.options.type) {
      case 'events':
        this.results = this.results.map(x => {
          x.distance = x.isOnline || !x.location?.location ? 1000000000 : calculateDistance(sourceLocation, x.location.location);
          return x;
        }).sort((x, y) => {
          if (x.distance === y.distance) {
            return (x.timestamp > y.timestamp ? 1 : -1);
          } else {
            return (x.distance > y.distance ? 1 : -1);
          }
        });
        break;
      default:
        this.results = this.results.map(x => {
          x.distance = calculateDistance(sourceLocation, x.location);
          return x;
        }).sort((x, y) => {
          return (x.distance > y.distance ? 1 : -1);
        });
        break;
    }
  }

  @Event({
    eventName: 'addMarker',
    composed: true,
    cancelable: true,
    bubbles: true
  }) addMarker: EventEmitter;

  @Event({
    eventName: 'inputFocused',
    composed: true,
    cancelable: true,
    bubbles: true
  }) inputFocused: EventEmitter;

  private toggleFilterDiv() {
    if (this.filterDiv.classList.contains('visible')) {
      this.filterDiv.classList.add('hidden');
      this.filterDiv.classList.remove('visible');
    } else {
      this.filterDiv.classList.remove('hidden');
      window.setTimeout(() => this.filterDiv.classList.add('visible'), 600);
    }
    // Close all dropdowns
    this.inputFocused.emit(this);
  }

  // Render
  render() {
    const noResults = !this.loading && (!this.results || this.getResultCount() < 1);
    const datePickers = this.options.filter?.filter(entry => entry.dropdown.type == 'date');
    const filters = this.options.filter?.filter(entry => entry.dropdown.type != 'date');
    const formStyle = { backgroundColor: this.options.color ? this.options.color : null };

    return (
      <Host class={this.sectionBehavior.hostClass}>
        <section style={this.sectionBehavior.sectionStyle} class={`${this.sectionBehavior.sectionClass} ${this.options.type}`}>
        { renderBlockMargin(this.sectionBehavior, VerticalPositionTop) }
          <div class={`section-inner ${this.loading ? 'loading' : ''} ${this.error ? 'error' : ''} ${noResults ? 'no-results' : ''}`}>
            {this.options.headline ? <h2>{this.options.headline}</h2> : ''}
            {this.options.subHeadline ? <h3>{this.options.subHeadline}</h3> : ''}

            {this.dataLoaded && this.showFilter ?

              <div class="form-wrapper">
                <form id={this.options.name} action={this.options.action}>
                  <div class={`form-container`} style={formStyle}>
                    {
                      this.options.showMap
                        ? <div class={`filter-map`}>
                          <cms-filter-map debug={this.debug} config={this.options.mapConfig} update={this.updateMarker} />
                        </div>
                        : ''
                    }
                    {
                      this.options.filter?.length > 0 || this.options.sort || this.options.labels.reset
                        ? <div class={`filter-buttons`}>
                          {
                            this.options.filter?.length > 0
                              ? <div class={`filter-button i-filter`}
                                     role="button"
                                     tabIndex={0}
                                     onKeyUp={(ev) => {
                                       if (ev.key === 'Enter') this.toggleFilterDiv();
                                     }}
                                     onClick={() => this.toggleFilterDiv()}
                                     aria-label={this.options.labels.filterButtonAlt}
                              >
                                  <span>
                                    {this.options.labels.filterButton ? this.options.labels.filterButton : 'Filter'}
                                  </span>
                              </div>
                              : ''
                          }
                          {
                            this.options.sort
                              ? this.getComponentForType(SORT_STRING, this.options.sort)
                              : ''
                          }
                          {
                            this.options.labels.reset
                              ? <div class="resetButton">
                                <cms-reset-button config={{
                                  label: this.options.labels.reset,
                                  form: this.options.name,
                                }} />
                              </div>
                              : ''
                          }
                        </div>
                        : ''
                    }
                    {
                      this.options.filter?.length > 0
                        ? <div class={`filter-wrapper ${this.options.expandFilter ? 'visible' : 'hidden'}`}
                               ref={(divElement: HTMLDivElement) => (this.filterDiv = divElement)}
                        >
                          <div class="filter">
                            {filters ?
                              filters.sort((a, b) => a.position > b.position ? 1 : -1).map(entry => (this.getComponentForType(FILTER_STRING, entry)))
                              : ''
                            }
                            {datePickers && datePickers.length > 0
                              ? <div class={`filter-date-picker`}>
                                {
                                  datePickers.sort((a, b) => a.position > b.position ? 1 : -1).map(entry => (this.getComponentForType(FILTER_STRING, entry)))
                                }
                              </div>
                              : ''
                            }
                          </div>
                        </div>
                        : ''
                    }
                  </div>
                </form>
                {
                  this.options.showMap
                    ? <div class={CmsFilterMap.mapClass}>
                      <div class={`map-canvas ${this.options.type}`} id={CmsFilterMap.mapCanvasId}
                           style={{ backgroundColor: 'rgb(229, 227, 223)', overflow: 'hidden' }} />
                    </div>
                    : ''
                }
              </div>

              : ''}

            {
              this.dataLoaded && this.results && this.getResultCount() >= 0 && this.getResultsToShow() > 0
                ? this.loadHighlightForType()
                : ''
            }

            <div class="content">
              {
                this.dataLoaded && this.results && this.getResultCount() >= 0
                  ? this.loadContentForType()
                  : ''
              }
            </div>

            {
              this.dataLoaded && this.results && !this.getIsEndOfResults()
                ?
                  <div class="more">
                    <cms-link-or-button
                        debug={this.debug}
                        config={JSON.stringify({
                          label: this.options.labels.more,
                          link: 'javascript:void()',
                          button: {
                            icon: 'i-arrow-down',
                            layout: 'default'
                          }
                        })}
                        callback={this.handleMoreButton.bind(this)}
                      />
                  </div>
                : ''
            }

            <div class="loader-wrapper">
              <cms-loading debug={this.debug} config={{label: this.options.labels.loading}}/>
            </div>

            <div class="error-wrapper">
              <div class="error-text">
                { this.results && this.getResultCount() < 1
                  ? [
                    <p class={`error-headline`}>
                      {this.showResultsOnLoad ? this.options.labels.noResults : this.options.labels.noResultsOnLoad}
                    </p>,
                    <p>
                      {this.showResultsOnLoad ? this.options.labels.noResultsHint : this.options.labels.noResultsOnLoadHint}
                    </p>,
                    this.options.labels.reset && this.showResultsOnLoad
                      ? <div class="resetButton">
                        <cms-reset-button config={{
                          label: this.options.labels.reset,
                          form: this.options.name,
                          layout: 'default',
                          icon: 'i-close',
                          noMargin: true
                        }} />
                      </div>
                      : ''
                  ]
                  : this.options.labels.error
                }
              </div>
            </div>
          </div>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionBottom) }
        </section>
      </Host>
    );
  }
}

export interface CmsFilterFormConfig {
  name?: string;
  action?: string;
  jsonUrl?: string;
  serviceUrl?: string;
  jsonMapKey?: string;
  initialSort?: string | CmsSortConfig;
  prefilter?: CmsFilterFormPrefilter[];
  setUrlParams?: boolean;
  expandFilter?: boolean;
  type: CmsFilterFormType;
  headline?: string;
  subHeadline?: string;
  labels: {
    filterButton: string;
    filterButtonAlt: string;
    reset: string;
    loading: string;
    error: string;
    more: string;
    noResults: string;
    noResultsHint: string;
    noResultsOnLoad: string;
    noResultsOnLoadHint: string;
    dateRange: string;
    markerHeadline: string;
    datepickerToday?: string;
    datepickerPrev?: string;
    datepickerNext?: string;
  };
  locale: string; // language locale e.g. de-de
  fsLocale: string;
  sort: CmsFilterFormSelect;
  filter: CmsFilterFormSelect[];
  perPage: number;
  showMap: boolean;
  mapConfig?: CmsFilterMapConfig;
  color?: string;
}

interface CmsSortConfig {
  key: string;
  reverse?: boolean;
}

interface CmsFilterFormPrefilter {
  key: string;
  value: string[] | number[] | string | boolean | number;
}

interface CmsFilterFormSelect {
  dataConfig: CmsFilterFormFieldDataConfig;
  sortByValue: boolean;
  sortReverse: boolean;
  width: string;
  dropdown: CmsInputDropdownConfig;
  position: number;
  hiddenByActiveMap: boolean;
}

interface CmsFilterFormFieldDataConfig {
  value: string; // use name of dropdown for filter key mapping; use key to fill filter dropdown
  label: string;
}

interface CmsFilterServicePayload {
  amount?: number,
  page?: number,
  locale?: string,
  fsLocale?: string,
  preFilter?: object,
  filter?: object,
  availableFilters?: object,
  zipKey?: MapsLatLng,
  sort?: CmsSortConfig
}

type CmsFilterFormFieldType = 'sort' | 'filter' | 'map' | 'search';

export type CmsFilterFormType = 'events' | 'partner' | 'news' | 'cooks';
